import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useOrganisations,
  useOrganisation,
  useOrganisationMembers,
  useCreateOrganisation,
  useAddOrgMember,
  useRemoveOrgMember,
} from '@/hooks/useOrganisations';
import { useUIStore } from '@/stores/uiStore';

export default function CompanyPage() {
  const navigate = useNavigate();
  const { orgId } = useParams();

  if (orgId) {
    return (
      <OrgDetail
        orgId={Number(orgId)}
        onBack={() => navigate('/company')}
      />
    );
  }

  return (
    <OrgList
      onSelect={(id) => navigate(`/company/${id}`)}
      onBack={() => navigate(-1)}
    />
  );
}

// ── Org List ─────────────────────────────────────────────────────────────────

function OrgList({ onSelect, onBack }) {
  const addToast = useUIStore((s) => s.addToast);
  const { data: orgs = [], isLoading: orgsLoading } = useOrganisations();
  const { mutate: createOrg, isPending } = useCreateOrganisation();

  const [form, setForm] = useState({ label: '', description: '' });
  const [errors, setErrors] = useState({});

  function validate() {
    const e = {};
    if (!form.label.trim()) e.label = 'Company name is required.';
    return e;
  }

  function handleChange(e) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setErrors((prev) => ({ ...prev, [e.target.name]: undefined }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) { setErrors(e2); return; }

    createOrg(
      { label: form.label.trim(), description: form.description.trim() },
      {
        onSuccess: () => {
          addToast({ title: 'Company created', variant: 'success' });
          setForm({ label: '', description: '' });
        },
        onError: (err) => {
          addToast({
            title: 'Failed to create company',
            description: err?.response?.data?.error || 'Please try again.',
            variant: 'destructive',
          });
        },
      }
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-12">
      <h1 className="mb-1 text-2xl font-medium text-ink">Manage Organization</h1>
      <p className="mb-8 text-sm text-neutral-500">
        Create or view the organisations tied to your licence.
      </p>

      {/* Existing organisations */}
      {orgsLoading ? (
        <div className="mb-10 space-y-2">
          {[1, 2].map((n) => (
            <div key={n} className="h-12 animate-pulse rounded-xl bg-paper" />
          ))}
        </div>
      ) : orgs.length > 0 ? (
        <section className="mb-10">
          <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-neutral-400">
            Your organisations
          </h2>
          <ul className="divide-y divide-neutral-200 rounded-xl border border-neutral-200 bg-paper overflow-hidden">
            {orgs.map((org) => (
              <li key={org.id}>
                <button
                  onClick={() => onSelect(org.id)}
                  className="flex w-full items-center justify-between px-5 py-3 text-sm text-ink hover:bg-parchment transition-colors text-left group"
                >
                  <span className="font-medium">{org.label}</span>
                  <svg
                    className="h-4 w-4 text-neutral-400 group-hover:text-ink transition-colors"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Create form */}
      <section className="rounded-xl border border-neutral-200 bg-paper p-6">
        <h2 className="mb-5 text-base font-medium text-ink">Create a new company</h2>
        <form onSubmit={handleSubmit} noValidate className="space-y-5">
          <Field
            label="Company name"
            name="label"
            value={form.label}
            onChange={handleChange}
            error={errors.label}
            placeholder="Acme Corp"
            required
          />
          <Field
            label="Description"
            name="description"
            as="textarea"
            value={form.description}
            onChange={handleChange}
            error={errors.description}
            placeholder="A brief description of your organisation (optional)"
            rows={3}
          />

          <div className="flex items-center gap-3 pt-1">
            <button
              type="submit"
              disabled={isPending}
              className="rounded-lg bg-[#003cc5] px-5 py-2 text-sm font-semibold text-parchment shadow transition hover:bg-[#0036b1] active:scale-95 disabled:opacity-50"
            >
              {isPending ? 'Creating…' : 'Create company'}
            </button>
            <button
              type="button"
              onClick={onBack}
              className="rounded-lg px-4 py-2 text-sm text-neutral-500 transition hover:bg-parchment hover:text-ink"
            >
              Cancel
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

// ── Org Detail ────────────────────────────────────────────────────────────────

function OrgDetail({ orgId, onBack }) {
  const addToast = useUIStore((s) => s.addToast);
  const { data: org, isLoading: orgLoading } = useOrganisation(orgId);
  const { data: members = [], isLoading: membersLoading } = useOrganisationMembers(orgId);
  const { mutate: addMember, isPending: addPending } = useAddOrgMember(orgId);
  const { mutate: removeMember, isPending: removePending } = useRemoveOrgMember(orgId);

  const [addEmail, setAddEmail] = useState('');
  const [addError, setAddError] = useState('');
  const [removingUid, setRemovingUid] = useState(null);

  function handleAddMember(e) {
    e.preventDefault();
    const email = addEmail.trim();
    if (!email) { setAddError('Email is required.'); return; }
    setAddError('');

    addMember({ email }, {
      onSuccess: () => {
        addToast({ title: 'Member added', variant: 'success' });
        setAddEmail('');
      },
      onError: (err) => {
        const msg = err?.response?.data?.error || 'Could not add member.';
        setAddError(msg);
        addToast({ title: 'Failed to add member', description: msg, variant: 'destructive' });
      },
    });
  }

  function handleRemoveMember(uid, displayName) {
    if (!confirm(`Remove ${displayName} from this organisation?`)) return;
    setRemovingUid(uid);
    removeMember(uid, {
      onSuccess: () => {
        addToast({ title: 'Member removed', variant: 'success' });
        setRemovingUid(null);
      },
      onError: (err) => {
        addToast({
          title: 'Failed to remove member',
          description: err?.response?.data?.error || 'Please try again.',
          variant: 'destructive',
        });
        setRemovingUid(null);
      },
    });
  }

  if (orgLoading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12 space-y-4">
        <div className="h-6 w-40 animate-pulse rounded bg-paper" />
        <div className="h-40 animate-pulse rounded-xl bg-paper" />
        <div className="h-60 animate-pulse rounded-xl bg-paper" />
      </div>
    );
  }

  if (!org) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12">
        <p className="text-sm text-neutral-500">Organisation not found.</p>
        <BackButton onClick={onBack} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 space-y-8">
      {/* Back + header */}
      <div>
        <BackButton onClick={onBack} />
        <h1 className="mt-4 text-2xl font-medium text-ink">{org.label}</h1>
        {org.type_label && (
          <span className="mt-1 inline-block rounded-full bg-paper px-2.5 py-0.5 text-xs text-neutral-500 border border-neutral-200">
            {org.type_label}
          </span>
        )}
      </div>

      {/* Details block */}
      <section className="rounded-xl border border-neutral-200 bg-paper p-6">
        <h2 className="mb-4 text-sm font-medium uppercase tracking-wide text-neutral-400">
          Organisation details
        </h2>
        <dl className="space-y-3 text-sm">
          {org.owner && (
            <DetailRow label="Owner" value={org.owner.display_name} />
          )}
          {org.created && (
            <DetailRow
              label="Created"
              value={new Date(org.created * 1000).toLocaleDateString(undefined, {
                year: 'numeric', month: 'long', day: 'numeric',
              })}
            />
          )}
          {org.description && (
            <DetailRow label="Description" value={org.description} />
          )}
          {org.address && formatAddress(org.address) && (
            <DetailRow label="Address" value={formatAddress(org.address)} />
          )}
          {org.phone && <DetailRow label="Phone" value={org.phone} />}
          {org.website && (
            <div className="flex gap-4">
              <dt className="w-28 shrink-0 text-neutral-400">Website</dt>
              <dd className="text-ink">
                <a
                  href={org.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#003cc5] underline underline-offset-2 hover:text-[#0036b1]"
                >
                  {org.website}
                </a>
              </dd>
            </div>
          )}
        </dl>
      </section>

      {/* Sub-organisations */}
      {(org.subgroups?.length > 0) && (
        <section className="rounded-xl border border-neutral-200 bg-paper p-6">
          <h2 className="mb-4 text-sm font-medium uppercase tracking-wide text-neutral-400">
            Sub-organisations
          </h2>
          <ul className="divide-y divide-neutral-200">
            {org.subgroups.map((sub) => (
              <li key={sub.id} className="flex items-center justify-between py-2.5">
                <span className="text-sm font-medium text-ink">{sub.label}</span>
                <span className="text-xs text-neutral-400">{sub.type_label}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Members */}
      <section className="rounded-xl border border-neutral-200 bg-paper p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-medium uppercase tracking-wide text-neutral-400">
            Members
            {org.member_count != null && (
              <span className="ml-2 rounded-full bg-parchment px-2 py-0.5 text-xs text-neutral-500">
                {org.member_count}
              </span>
            )}
          </h2>
        </div>

        {/* Add member form */}
        <form onSubmit={handleAddMember} className="mb-5 flex gap-2">
          <div className="flex-1">
            <input
              type="email"
              value={addEmail}
              onChange={(e) => { setAddEmail(e.target.value); setAddError(''); }}
              placeholder="Add by email address…"
              className={`w-full rounded-lg border px-3 py-2 text-sm text-ink bg-document placeholder:text-neutral-400 outline-none transition focus:ring-2 focus:ring-[#003cc5]/30 focus:border-[#003cc5] ${
                addError ? 'border-[#003cc5]' : 'border-neutral-200'
              }`}
            />
            {addError && <p className="mt-1 text-xs text-[#003cc5]">{addError}</p>}
          </div>
          <button
            type="submit"
            disabled={addPending}
            className="rounded-lg bg-[#003cc5] px-4 py-2 text-sm font-semibold text-parchment shadow transition hover:bg-[#0036b1] active:scale-95 disabled:opacity-50 whitespace-nowrap"
          >
            {addPending ? 'Adding…' : 'Add member'}
          </button>
        </form>

        {/* Member list */}
        {membersLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((n) => (
              <div key={n} className="h-10 animate-pulse rounded-lg bg-parchment" />
            ))}
          </div>
        ) : members.length === 0 ? (
          <p className="text-sm text-neutral-400 italic">No members yet.</p>
        ) : (
          <ul className="divide-y divide-neutral-200">
            {members.map((m) => (
              <li key={m.uid} className="flex items-center justify-between py-2.5">
                <div>
                  <p className="text-sm font-medium text-ink">{m.display_name}</p>
                  <p className="text-xs text-neutral-400">{m.email}</p>
                </div>
                <button
                  onClick={() => handleRemoveMember(m.uid, m.display_name)}
                  disabled={removePending && removingUid === m.uid}
                  className="rounded-lg px-3 py-1.5 text-xs text-neutral-400 hover:text-[#003cc5] hover:bg-parchment transition disabled:opacity-40"
                >
                  {removePending && removingUid === m.uid ? 'Removing…' : 'Remove'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ── Small helpers ─────────────────────────────────────────────────────────────

function BackButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-ink transition"
    >
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
      </svg>
      All organisations
    </button>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex gap-4">
      <dt className="w-28 shrink-0 text-neutral-400">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  );
}

function formatAddress(addr) {
  if (!addr) return null;
  return [addr.line1, addr.line2, addr.city, addr.state, addr.postal_code, addr.country_code]
    .filter(Boolean)
    .join(', ');
}

function Field({ label, name, as: Tag = 'input', error, ...props }) {
  return (
    <div>
      <label htmlFor={name} className="mb-1.5 block text-sm font-medium text-ink">
        {label}
        {props.required && <span className="ml-1 text-[#003cc5]">*</span>}
      </label>
      <Tag
        id={name}
        name={name}
        className={`w-full rounded-lg border px-3 py-2 text-sm text-ink bg-document placeholder:text-neutral-400 outline-none transition focus:ring-2 focus:ring-[#003cc5]/30 focus:border-[#003cc5] ${
          error ? 'border-[#003cc5]' : 'border-neutral-200'
        }`}
        {...props}
      />
      {error && <p className="mt-1 text-xs text-[#003cc5]">{error}</p>}
    </div>
  );
}
